import React, { useEffect, useState } from "react";
import { UserPlus, ShieldCheck, Eye, PhoneCall, CreditCard, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import { Loading, PageHeader } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

const PERMS = [
  { v: "full_access", label: "Full Access", icon: ShieldCheck, desc: "Can view and manage everything" },
  { v: "view_only", label: "View Only", icon: Eye, desc: "Can see updates, cannot change anything" },
  { v: "emergency_contact", label: "Emergency Contact", icon: PhoneCall, desc: "Contacted in emergencies only" },
  { v: "billing", label: "Financial / Billing", icon: CreditCard, desc: "Can view and manage billing" },
];

export default function Members() {
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", relation: "", permission: "view_only" });
  const [showForm, setShowForm] = useState(false);
  const { user } = useAuth();
  const isOwner = user?.role === "family_owner";
  const load = () => api.get("/family/members").then((r) => setMembers(r.data));
  useEffect(() => { load().catch(() => setMembers([])); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/family/members", form);
      toast.success(r.data.message);
      setShowForm(false); setForm({ name: "", email: "", relation: "", permission: "view_only" }); load();
    } catch (err) { toast.error(formatErr(err)); }
  };
  if (!members) return <Loading />;
  return (
    <div data-testid="members-page">
      <PageHeader title="Family Members" sub="Everyone who can see your parent's care — with permissions you control."
        actions={isOwner && <button onClick={() => setShowForm(!showForm)} className="p1-btn-primary !px-5 !py-2.5" data-testid="invite-member-btn"><UserPlus className="h-4 w-4" /> Invite Member</button>} />
      {showForm && isOwner && (
        <form onSubmit={submit} className="p1-card mb-6 grid gap-4 p-5 sm:grid-cols-2" data-testid="invite-form">
          <div><label className="p1-label">Name *</label><input className="p1-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
          <div><label className="p1-label">Email *</label><input type="email" className="p1-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
          <div><label className="p1-label">Relation</label><input className="p1-input" value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })} placeholder="e.g. Daughter" data-testid="invite-relation" /></div>
          <div><label className="p1-label">Permission</label>
            <select className="p1-input" value={form.permission} onChange={(e) => setForm({ ...form, permission: e.target.value })} data-testid="invite-permission">
              {PERMS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
            </select></div>
          <div className="sm:col-span-2"><button className="p1-btn-primary !px-5 !py-2.5" data-testid="invite-submit">Send Invite</button></div>
        </form>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {members.map((m) => {
          const perm = PERMS.find((p) => p.v === m.permission);
          return (
            <div key={m.user.id} className="p1-card p-5" data-testid={`member-card-${m.user.id}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-900/10 font-heading font-bold text-emerald-900">{m.user.name?.[0]}</span>
                <div>
                  <p className="font-heading font-semibold text-stone-900">{m.user.name} {m.permission === "owner" && <Crown className="inline h-4 w-4 text-terracotta" />}</p>
                  <p className="text-xs text-stone-500">{m.user.email} · {m.relation}</p>
                </div>
              </div>
              {perm && <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-600"><perm.icon className="h-3.5 w-3.5" /> {perm.label} — {perm.desc}</p>}
              {m.permission === "owner" && <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-terracotta-soft px-3 py-1 text-xs font-semibold text-terracotta">Primary account holder</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
